import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../lib/apiClient';
import PrintButton from '../components/print/PrintButton';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ExportPrintButtons } from '../components/ExportPrintButtons';
import { Pagination } from '../components/Pagination';
import { LazyImage } from '../components/LazyImage';
import { ProductAutocomplete } from '../components/ProductAutocomplete';
import { DefectiveProducts } from '../components/DefectiveProducts';
import { toast } from 'sonner';
import { 
  Package, 
  Plus, 
  Search,
  X,
  Filter,
  Grid3X3,
  List,
  LayoutGrid,
  ArrowUpDown,
  SortAsc,
  SortDesc,
  FileSpreadsheet,
  Zap,
  Trash2,
  Pencil,
  Copy,
  CheckSquare,
  Square,
  AlertTriangle,
  Store,
  Globe
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

export default function ProductsPage() {
  const { t, isRTL, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // حالة النشر: على المتجر الإلكتروني وعلى WooCommerce (اختصارات التفعيل في القائمة)
  const [storeProductIds, setStoreProductIds] = useState(new Set());

  const fetchStoreProducts = async () => {
    if (!isAdmin) return;
    try {
      const res = await apiClient.get('/store/products');
      setStoreProductIds(new Set((res.data || []).map(sp => sp.product_id)));
    } catch { /* المتجر غير مفعّل — تبقى الأيقونات غير نشطة */ }
  };

  const toggleStoreProduct = async (product) => {
    const inStore = storeProductIds.has(product.id);
    try {
      if (inStore) {
        await apiClient.delete(`/store/products/${product.id}`);
        setStoreProductIds(prev => { const n = new Set(prev); n.delete(product.id); return n; });
        toast.success(language === 'ar' ? 'أُزيل من المتجر' : 'Retiré de la boutique');
      } else {
        await apiClient.post('/store/products', { product_id: product.id });
        setStoreProductIds(prev => new Set(prev).add(product.id));
        toast.success(language === 'ar' ? 'فُعّل على المتجر' : 'Activé sur la boutique');
      }
    } catch (e) {
      toast.error(language === 'ar' ? 'فشل تغيير حالة المتجر' : 'Échec boutique');
    }
  };

  const handleClone = async (product) => {
    try {
      await apiClient.post(`/products/${product.id}/clone`);
      toast.success(language === 'ar' ? 'تم استنساخ المنتج' : 'Produit cloné');
      fetchProducts();
    } catch (e) {
      toast.error(language === 'ar' ? 'فشل استنساخ المنتج' : 'Échec du clonage');
    }
  };

  const toggleWooProduct = async (product) => {    const published = product.woocommerce_status === 'published';
    try {
      if (published) {
        await apiClient.delete(`/woocommerce/unpublish-product/${product.id}`);
        setProducts(prev => prev.map(p => p.id === product.id ? { ...p, woocommerce_status: '' } : p));
        toast.success(language === 'ar' ? 'أُلغي النشر على WooCommerce' : 'Dépublié de WooCommerce');
      } else {
        await apiClient.post(`/woocommerce/publish-product/${product.id}`);
        setProducts(prev => prev.map(p => p.id === product.id ? { ...p, woocommerce_status: 'published' } : p));
        toast.success(language === 'ar' ? 'نُشر على WooCommerce' : 'Publié sur WooCommerce');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || (language === 'ar' ? 'فشل النشر على WooCommerce' : 'Échec WooCommerce'));
    }
  };
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [modelFilter, setModelFilter] = useState(searchParams.get('model') || '');
  const [familyFilter, setFamilyFilter] = useState(searchParams.get('family') || '');
  const [families, setFamilies] = useState([]);
  const [viewMode, setViewMode] = useState(localStorage.getItem('productsViewMode') || 'grid'); // grid, list, compact
  // عرض أعمدة العرض القائمي (قابل للسحب) — يُحفظ محلياً
  const DEFAULT_COL_W = [1.1, 2.4, 1.1, 1.0, 0.9, 0.9, 0.9, 0.7, 0.6];
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('productsListColW'));
      return Array.isArray(saved) && saved.length === 9 ? saved : DEFAULT_COL_W;
    } catch { return DEFAULT_COL_W; }
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [useQuickSearch, setUseQuickSearch] = useState(localStorage.getItem('useQuickSearch') === 'true');
  const [activeTab, setActiveTab] = useState('products'); // products, defective
  
  // Selection state for bulk delete
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(localStorage.getItem('productsPerPage')) || 20);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Toggle selection mode
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedProducts(new Set());
    }
  };

  // Toggle single product selection
  const toggleProductSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Select all products on current page
  const selectAllProducts = () => {
    const newSelected = new Set(selectedProducts);
    sortedProducts.forEach(p => newSelected.add(p.id));
    setSelectedProducts(newSelected);
  };

  // Deselect all products
  const deselectAllProducts = () => {
    setSelectedProducts(new Set());
  };

  // Delete selected products
  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Delete products one by one
      const deletePromises = Array.from(selectedProducts).map(id =>
        apiClient.delete(`/products/${id}`, { headers }).catch(err => ({ error: true, id }))
      );
      
      const results = await Promise.all(deletePromises);
      const failures = results.filter(r => r.error);
      
      if (failures.length === 0) {
        toast.success(language === 'ar' 
          ? `تم حذف ${selectedProducts.size} منتج بنجاح` 
          : `${selectedProducts.size} produits supprimés`);
      } else {
        toast.warning(language === 'ar' 
          ? `تم حذف ${selectedProducts.size - failures.length} منتج، فشل حذف ${failures.length}` 
          : `${selectedProducts.size - failures.length} supprimés, ${failures.length} échoués`);
      }
      
      setSelectedProducts(new Set());
      setSelectMode(false);
      setShowDeleteDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحذف' : 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('productsViewMode', mode);
  };

  const toggleQuickSearch = () => {
    const newValue = !useQuickSearch;
    setUseQuickSearch(newValue);
    localStorage.setItem('useQuickSearch', newValue.toString());
  };

  const handleQuickSelect = (product) => {
    navigate(isAdmin ? `/products/${product.id}/edit` : '/products');
  };

  const handleItemsPerPageChange = (newValue) => {
    setItemsPerPage(newValue);
    setCurrentPage(1);
    localStorage.setItem('productsPerPage', newValue.toString());
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name': {
        const nameA = language === 'ar' ? (a.name_ar || a.name_en) : (a.name_en || a.name_ar);
        const nameB = language === 'ar' ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar);
        comparison = (nameA || '').localeCompare(nameB || '');
        break;
      }
      case 'price':
        comparison = (a.retail_price || 0) - (b.retail_price || 0);
        break;
      case 'stock':
        comparison = (a.quantity || 0) - (b.quantity || 0);
        break;
      case 'purchase_price':
        comparison = (a.purchase_price || 0) - (b.purchase_price || 0);
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (modelFilter) params.set('model', modelFilter);
      if (familyFilter) params.set('family_id', familyFilter);
      params.set('page', currentPage.toString());
      params.set('page_size', itemsPerPage.toString());
      
      const response = await apiClient.get(`/products/paginated?${params.toString()}`);
      setProducts(response.data.items);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to non-paginated endpoint
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.set('search', searchQuery);
        if (modelFilter) params.set('model', modelFilter);
        if (familyFilter) params.set('family_id', familyFilter);
        const response = await apiClient.get(`/products?${params.toString()}`);
        setProducts(response.data);
        setTotalItems(response.data.length);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [searchQuery, modelFilter, familyFilter, currentPage, itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStoreProducts();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/product-families');
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setFamilies(list.filter(f => f && f.id));
      } catch { /* العائلات غير متاحة — يبقى الفلتر فارغاً */ }
    })();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (modelFilter) params.set('model', modelFilter);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setModelFilter('');
    setFamilyFilter('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const getStockBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">{t.outOfStock}</Badge>;
    } else if (quantity < 10) {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">{t.lowStockWarning}</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{t.inStock}</Badge>;
  };

  // ── Resizable list columns ──
  const listGridTemplate = `${selectMode ? '36px ' : ''}${colWidths.map(w => `${w}fr`).join(' ')}`;

  const startColResize = (idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = [...colWidths];
    const container = e.currentTarget.closest('[data-list-header]');
    const totalFr = startW.reduce((a, b) => a + b, 0);
    const selPx = selectMode ? 36 : 0;
    const pxPerFr = container ? Math.max(1, (container.getBoundingClientRect().width - selPx) / totalFr) : 100;
    const rtlFactor = document.dir === 'rtl' ? -1 : 1;
    const onMove = (ev) => {
      const dfr = ((ev.clientX - startX) * rtlFactor) / pxPerFr;
      setColWidths(() => {
        const next = [...startW];
        next[idx] = Math.max(0.35, Math.round((startW[idx] + dfr) * 100) / 100);
        return next;
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setColWidths(prev => {
        localStorage.setItem('productsListColW', JSON.stringify(prev));
        return prev;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const ColHeader = ({ idx, className = '', children }) => (
    <div className={`relative ${className}`}>
      {children}
      {idx < 8 && (
        <span
          onMouseDown={startColResize(idx)}
          onDoubleClick={(e) => { e.stopPropagation(); setColWidths(prev => { const n = [...prev]; n[idx] = DEFAULT_COL_W[idx]; localStorage.setItem('productsListColW', JSON.stringify(n)); return n; }); }}
          className="absolute top-0 bottom-0 w-2 cursor-col-resize select-none hover:bg-primary/30 rounded"
          style={{ insetInlineEnd: '-4px' }}
          title={language === 'ar' ? 'اسحب لتغيير عرض العمود — نقرة مزدوجة لإعادة الضبط' : 'Glisser pour redimensionner — double-clic pour réinitialiser'}
          data-testid={`col-resize-${idx}`}
        />
      )}
    </div>
  );

  // Quick export to Excel function
  const quickExportToExcel = () => {
    const headers = [
      language === 'ar' ? 'الكود' : 'Code',
      language === 'ar' ? 'المنتج' : 'Produit',
      language === 'ar' ? 'العائلة' : 'Famille',
      language === 'ar' ? 'المخزون' : 'Stock',
      language === 'ar' ? 'سعر البيع' : 'Prix vente',
      language === 'ar' ? 'سعر الشراء' : 'Prix achat'
    ];
    
    let tableHtml = `<table><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>`;
    products.forEach(p => {
      tableHtml += `<tr>
        <td>${p.article_code || '-'}</td>
        <td>${language === 'ar' ? p.name_ar : p.name_en}</td>
        <td>${p.family_name || '-'}</td>
        <td>${p.quantity}</td>
        <td>${p.retail_price?.toFixed(2) || '0.00'}</td>
        <td>${p.purchase_price?.toFixed(2) || '0.00'}</td>
      </tr>`;
    });
    tableHtml += '</table>';
    
    const blob = new Blob([`<html><head><meta charset="UTF-8"></head><body>${tableHtml}</body></html>`], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products_${new Date().toISOString().split('T')[0]}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t.products}</h1>
            <p className="text-muted-foreground mt-1">
              {totalItems} {t.products.toLowerCase()}
              {selectMode && selectedProducts.size > 0 && (
                <span className="ms-2 text-primary font-medium">
                  ({selectedProducts.size} {language === 'ar' ? 'محدد' : 'sélectionnés'})
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {activeTab === 'products' && (
              <>
                {/* Bulk Selection Mode Toggle */}
                <Button 
                  variant={selectMode ? "default" : "outline"} 
                  onClick={toggleSelectMode} 
                  className="gap-2"
                  data-testid="bulk-select-btn"
                >
                  {selectMode ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  <span className="hidden sm:inline">{language === 'ar' ? 'تحديد متعدد' : 'Sélection multiple'}</span>
                </Button>
                
                {/* Bulk Actions */}
                {selectMode && selectedProducts.size > 0 && (
                  <>
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowDeleteDialog(true)}
                      className="gap-2"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                      {language === 'ar' ? `حذف (${selectedProducts.size})` : `Supprimer (${selectedProducts.size})`}
                    </Button>
                    <Button variant="ghost" onClick={deselectAllProducts} size="sm">
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
                
                {selectMode && (
                  <Button variant="outline" onClick={selectAllProducts} size="sm">
                    {language === 'ar' ? 'تحديد الكل' : 'Tout sélectionner'}
                  </Button>
                )}
                
                {/* Quick Excel Export Button */}
                <Button variant="outline" onClick={quickExportToExcel} className="gap-2" data-testid="quick-export-excel-btn">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="hidden sm:inline">Excel</span>
                </Button>
                <Link to="/products/add">
                  <Button className="gap-2" data-testid="add-product-btn">
                    <Plus className="h-5 w-5" />
                    {t.addProduct}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="products" className="gap-2" data-testid="products-tab">
              <Package className="h-4 w-4" />
              {language === 'ar' ? 'المنتجات' : 'Produits'}
            </TabsTrigger>
            <TabsTrigger value="defective" className="gap-2" data-testid="defective-tab">
              <AlertTriangle className="h-4 w-4" />
              {language === 'ar' ? 'المعطلة' : 'Défectueux'}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="defective" className="mt-6">
            <DefectiveProducts />
          </TabsContent>
          
          <TabsContent value="products" className="mt-6 space-y-6">
            {/* Bulk Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {language === 'ar' ? 'تأكيد الحذف' : 'Confirmer la suppression'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {language === 'ar' 
                      ? `هل أنت متأكد من حذف ${selectedProducts.size} منتج؟ لا يمكن التراجع عن هذا الإجراء.`
                      : `Êtes-vous sûr de supprimer ${selectedProducts.size} produits? Cette action est irréversible.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>
                    {language === 'ar' ? 'إلغاء' : 'Annuler'}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleBulkDelete}
                    disabled={deleting}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {deleting 
                  ? (language === 'ar' ? 'جارٍ الحذف...' : 'Suppression...') 
                  : (language === 'ar' ? 'حذف' : 'Supprimer')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Search & Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'طريقة العرض:' : 'Affichage:'}
                </span>
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => changeViewMode('grid')}
                    className="rounded-r-none"
                    data-testid="view-grid-btn"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => changeViewMode('list')}
                    className="rounded-none border-x"
                    data-testid="view-list-btn"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'compact' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => changeViewMode('compact')}
                    className="rounded-l-none"
                    data-testid="view-compact-btn"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Export & Print */}
                <ExportPrintButtons
                  data={products.map(p => ({
                    article_code: p.article_code || '-',
                    name: language === 'ar' ? p.name_ar : p.name_en,
                    family: p.family_name || '-',
                    stock: p.quantity,
                    price: p.retail_price?.toFixed(2) || '0.00',
                    purchase_price: p.purchase_price?.toFixed(2) || '0.00'
                  }))}
                  columns={[
                    { key: 'article_code', label: language === 'ar' ? 'الكود' : 'Code' },
                    { key: 'name', label: language === 'ar' ? 'المنتج' : 'Produit' },
                    { key: 'family', label: language === 'ar' ? 'العائلة' : 'Famille' },
                    { key: 'stock', label: language === 'ar' ? 'المخزون' : 'Stock' },
                    { key: 'price', label: language === 'ar' ? 'السعر' : 'Prix' },
                    { key: 'purchase_price', label: language === 'ar' ? 'سعر الشراء' : 'Prix achat' }
                  ]}
                  filename="products"
                  title={language === 'ar' ? 'قائمة المنتجات' : 'Liste des produits'}
                  language={language}
                />
              </div>
              
              {/* Sort Controls */}
              <div className="flex items-center gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px]">
                    <ArrowUpDown className="h-4 w-4 me-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">{language === 'ar' ? 'الاسم' : 'Nom'}</SelectItem>
                    <SelectItem value="price">{language === 'ar' ? 'السعر' : 'Prix'}</SelectItem>
                    <SelectItem value="stock">{language === 'ar' ? 'المخزون' : 'Stock'}</SelectItem>
                    <SelectItem value="purchase_price">{language === 'ar' ? 'سعر الشراء' : 'Prix achat'}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'تصاعدي' : 'تنازلي'}
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              {useQuickSearch ? (
                <ProductAutocomplete
                  onSelect={handleQuickSelect}
                  placeholder={t.searchPlaceholder}
                  className="flex-1"
                  clearOnSelect={false}
                />
              ) : (
                <div className="relative flex-1">
                  <Search className={`absolute top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                    type="text"
                    placeholder={t.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`h-11 ${isRTL ? 'pr-10' : 'pl-10'}`}
                    data-testid="product-search-input"
                  />
                </div>
              )}
              <Button
                type="button"
                variant={useQuickSearch ? "default" : "outline"}
                size="icon"
                onClick={toggleQuickSearch}
                title={language === 'ar' ? 'بحث فوري' : 'Recherche rapide'}
                className="h-11 w-11"
              >
                <Zap className={`h-5 w-5 ${useQuickSearch ? 'text-yellow-300' : ''}`} />
              </Button>
              <div className="relative flex-1 sm:max-w-xs">
                <Filter className={`absolute top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input
                  type="text"
                  placeholder={t.filterByModel}
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  className={`h-11 ${isRTL ? 'pr-10' : 'pl-10'}`}
                  data-testid="model-filter-input"
                />
              </div>
              <Select value={familyFilter || 'all'} onValueChange={(v) => { setFamilyFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
                <SelectTrigger className="h-11 flex-1 sm:max-w-[200px]" data-testid="family-filter-select">
                  <SelectValue placeholder={language === 'ar' ? 'كل العائلات' : 'Toutes les familles'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'ar' ? 'كل العائلات' : 'Toutes les familles'}</SelectItem>
                  {families.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name_ar || f.name || f.name_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchQuery || modelFilter || familyFilter) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearFilters}
                  className="gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4" />
                  {t.clearFilters}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="spinner" />
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="empty-state py-16">
            <Package className="h-20 w-20 text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium">{t.noProducts}</h3>
            <p className="text-muted-foreground mt-2">{t.noProductsSubtitle}</p>
            {isAdmin && (
              <Link to="/products/add" className="mt-6">
                <Button className="gap-2">
                  <Plus className="h-5 w-5" />
                  {t.addProduct}
                </Button>
              </Link>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* List View - Table style with all info */
          <div className="border rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid gap-2 p-3 bg-muted/50 text-xs font-medium border-b" style={{ gridTemplateColumns: listGridTemplate }} data-list-header="true">
              {selectMode && <div className="w-8"></div>}
              <ColHeader idx={0}>{language === 'ar' ? 'كود المنتج' : 'Code Article'}</ColHeader>
              <ColHeader idx={1}>{language === 'ar' ? 'اسم المنتج' : 'Nom d\'article'}</ColHeader>
              <ColHeader idx={2}>{language === 'ar' ? 'العائلة' : 'Famille'}</ColHeader>
              <ColHeader idx={3} className="text-center">{language === 'ar' ? 'الكمية المتوفرة' : 'Quantité'}</ColHeader>
              <ColHeader idx={4} className="text-center">{language === 'ar' ? 'سعر الشراء' : 'Prix achat'}</ColHeader>
              <ColHeader idx={5} className="text-center">{language === 'ar' ? 'سعر البيع' : 'Prix vente'}</ColHeader>
              <ColHeader idx={6} className="text-center">{language === 'ar' ? 'آخر شراء' : 'Dernier Achat'}</ColHeader>
              <ColHeader idx={7} className="text-center">{language === 'ar' ? 'تعديل' : 'Modifier'}</ColHeader>
              <ColHeader idx={8} className="text-center">{language === 'ar' ? 'طباعة' : 'Imprimer'}</ColHeader>
            </div>
            {/* Table Body */}
            <div className="divide-y">
              {sortedProducts.map((product) => (
                <div
                  key={product.id}
                  className={`block hover:bg-muted/30 transition-colors ${selectedProducts.has(product.id) ? 'bg-primary/5' : ''}`}
                  data-testid={`product-item-${product.id}`}
                >
                  <div className="grid gap-2 p-3 items-center text-sm" style={{ gridTemplateColumns: listGridTemplate }}>
                    {/* Selection Checkbox */}
                    {selectMode && (
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={selectedProducts.has(product.id)}
                          onCheckedChange={() => toggleProductSelection(product.id)}
                          data-testid={`select-product-${product.id}`}
                        />
                      </div>
                    )}
                    {/* Code Article */}
                    <Link to={isAdmin ? `/products/${product.id}/edit` : `/products/${product.id}`} className="font-mono text-xs text-blue-600 font-medium hover:underline">
                      {product.article_code || '-'}
                    </Link>
                    {/* Nom d'article */}
                    <Link to={isAdmin ? `/products/${product.id}/edit` : `/products/${product.id}`} className="flex items-center gap-2">
                      <img
                        src={product.image_url || 'https://images.unsplash.com/photo-1634403665443-81dc4d75843a?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt=""
                        className="w-8 h-8 object-cover rounded"
                      />
                      <span className="truncate font-medium">
                        {language === 'ar' ? product.name_ar : product.name_en}
                      </span>
                    </Link>
                    {/* Famille */}
                    <div className="text-muted-foreground text-xs">
                      {product.family_name || '-'}
                    </div>
                    {/* Stock */}
                    <div className="text-center">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-sm font-mono font-semibold" data-testid={`stock-num-${product.id}`}>{product.quantity ?? 0}</span>
                        {getStockBadge(product.quantity)}
                      </span>
                    </div>
                    {/* سعر الشراء */}
                    <div className="text-center text-sm">
                      {(product.purchase_price ?? 0).toFixed(2)} <span className="text-xs font-normal">{t.currency}</span>
                    </div>
                    {/* سعر البيع */}
                    <div className="text-center font-bold">
                      {(product.selling_price ?? product.retail_price ?? 0).toFixed(2)} <span className="text-xs font-normal">{t.currency}</span>
                    </div>
                    {/* Dernier Achat */}
                    <div className="text-center text-xs text-muted-foreground">
                      {product.last_purchase_date 
                        ? new Date(product.last_purchase_date).toLocaleDateString(language === 'ar' ? 'ar-DZ' : 'fr-FR')
                        : '-'}
                    </div>
                    {/* تعديل */}
                    <div className="flex justify-center gap-0.5">
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleStoreProduct(product)}
                            title={language === 'ar' ? (storeProductIds.has(product.id) ? 'إزالة من المتجر' : 'تفعيل على المتجر') : 'Boutique'}
                            data-testid={`toggle-store-${product.id}`}>
                            <Store className={`h-4 w-4 ${storeProductIds.has(product.id) ? 'text-green-600' : 'text-slate-400'}`} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleWooProduct(product)}
                            title={language === 'ar' ? (product.woocommerce_status === 'published' ? 'إلغاء النشر على WooCommerce' : 'نشر على WooCommerce') : 'WooCommerce'}
                            data-testid={`toggle-woo-${product.id}`}>
                            <Globe className={`h-4 w-4 ${product.woocommerce_status === 'published' ? 'text-green-600' : 'text-slate-400'}`} />
                          </Button>
                          <Link to={`/products/${product.id}/edit`} onClick={(e) => e.stopPropagation()} data-testid={`edit-product-${product.id}`}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleClone(product)}
                            title={language === 'ar' ? 'استنساخ المنتج' : 'Cloner le produit'}
                            data-testid={`clone-product-${product.id}`}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                    {/* Print */}
                    <div className="flex justify-center">
                      <PrintButton docType="product" record={product} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : viewMode === 'compact' ? (
          /* Compact View */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {sortedProducts.map((product) => (
              <div
                key={product.id}
                className={`relative block ${selectedProducts.has(product.id) ? 'ring-2 ring-primary' : ''}`}
                data-testid={`product-item-${product.id}`}
              >
                {selectMode && (
                  <div className="absolute top-1 right-1 z-10">
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                      className="bg-white shadow"
                    />
                  </div>
                )}
                <Link to={isAdmin ? `/products/${product.id}/edit` : `/products/${product.id}`}>
                  <div className="border rounded-lg p-2 bg-card hover:bg-muted/50 transition-colors text-center">
                    <LazyImage
                      src={product.image_url}
                      alt={language === 'ar' ? product.name_ar : product.name_en}
                      className="w-full aspect-square object-cover rounded-md mb-2"
                    />
                    <p className="text-xs font-medium truncate">{language === 'ar' ? product.name_ar : product.name_en}</p>
                    <p className="text-xs font-bold text-primary">{product.retail_price?.toFixed(0)} {t.currency}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          /* Grid View (default) */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedProducts.map((product) => (
              <div
                key={product.id}
                className={`relative block ${selectedProducts.has(product.id) ? 'ring-2 ring-primary' : ''}`}
                data-testid={`product-item-${product.id}`}
              >
                {selectMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                      className="bg-white shadow h-5 w-5"
                    />
                  </div>
                )}
                <Link to={isAdmin ? `/products/${product.id}/edit` : `/products/${product.id}`}>
                  <div className="product-card border rounded-xl overflow-hidden bg-card h-full flex flex-col">
                    <div className="product-image-container aspect-square relative">
                      <LazyImage
                        src={product.image_url}
                        alt={language === 'ar' ? product.name_ar : product.name_en}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'}`}>
                        {getStockBadge(product.quantity)}
                      </div>
                      {isAdmin && (
                        <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} flex gap-1`}>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleStoreProduct(product); }}
                            className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                            title={language === 'ar' ? 'تفعيل/إزالة من المتجر' : 'Boutique'}
                            data-testid={`toggle-store-card-${product.id}`}
                          >
                            <Store className={`h-4 w-4 ${storeProductIds.has(product.id) ? 'text-green-600' : 'text-slate-400'}`} />
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWooProduct(product); }}
                            className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                            title="WooCommerce"
                            data-testid={`toggle-woo-card-${product.id}`}
                          >
                            <Globe className={`h-4 w-4 ${product.woocommerce_status === 'published' ? 'text-green-600' : 'text-slate-400'}`} />
                          </button>
                          <Link
                            to={`/products/${product.id}/edit`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/products/${product.id}/edit`; }}
                            className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                            data-testid={`edit-product-card-${product.id}`}
                          >
                            <Pencil className="h-4 w-4 text-slate-700" />
                          </Link>
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="font-semibold text-lg line-clamp-1">
                        {language === 'ar' ? product.name_ar : product.name_en}
                      </h3>
                      <p className="text-muted-foreground text-sm mt-1 line-clamp-2 flex-1">
                        {language === 'ar' ? product.description_ar : product.description_en}
                      </p>
                      <div className="mt-4">
                        <p className="text-primary font-bold text-xl">
                          {(product.retail_price ?? product.price ?? 0).toFixed(2)} {t.currency}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t.quantity}: {product.quantity ?? 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {language === 'ar' ? 'سعر الشراء' : 'Prix achat'}: {(product.purchase_price ?? 0).toFixed(2)} {t.currency}
                        </p>
                      </div>
                      {product.compatible_models && product.compatible_models.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                            {t.compatibleModels}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {product.compatible_models.slice(0, 3).map((model, idx) => (
                              <span key={idx} className="model-badge">
                                {model}
                              </span>
                            ))}
                            {product.compatible_models.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{product.compatible_models.length - 3}
                              </Badge>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            className="mt-6"
          />
        )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
